// Static UI config for Gumroad bundle tab — data only, no API calls.
// Mirrors backend gumroad-bundle.js constants.

/** Mirrors backend GUMROAD_BUNDLE_PROMO_UI_STATE — UI rendering only. */
export const GUMROAD_BUNDLE_UI_STATE = {
  LOADING: 'loading',
  ERROR: 'error',
  NOT_CONFIGURED: 'not_configured',
  ALREADY_ENTITLED: 'already_entitled',
  HAS_PROMO: 'has_promo',
  ELIGIBLE: 'eligible',
  NOT_ELIGIBLE: 'not_eligible',
};

export const GUMROAD_BUNDLE_ELIGIBILITY_PATH = {
  GUMROAD_EMAIL: 'gumroad_email',
  TRANSFER_RECIPIENT: 'transfer_recipient',
};

export const GUMROAD_BUNDLE_SUPPORT_DISCORD_CHANNELS = [
  'support-tickets',
  'revoltgpt-unreal-plugin',
  'revolt-desktop-app',
];

/** Per-state section intros — avoids funnel copy on terminal states (e.g. already entitled). */
export const GUMROAD_BUNDLE_SECTION_INTROS = {
  error:
    'We could not determine your bundle promotion status. Try again or contact support if this continues.',
  not_configured:
    'Bundle promotion codes appear here when Gumroad integration is enabled on this server.',
  already_entitled:
    'Your account already includes Revolt bundle access. This tab is only for claiming a Gumroad bundle coupon when you do not yet have the bundle.',
  has_promo:
    'Your one-time promotion code is ready. Complete checkout on the bundle product page to activate access on this account.',
  eligible:
    'If you purchased the Gumroad UE plugin, you may qualify for a one-time promotion code toward the Revolt Lifetime Plan Bundle on the marketplace.',
  not_eligible:
    'This tab matches Gumroad UE plugin purchases to Revolt accounts for a bundle promotion code. See below if you believe your purchase should qualify.',
  not_eligible_transfer_pending:
    'Your Gumroad transfer is on file but not finalized yet. Once support completes it, you can request your bundle promotion code here.',
};

/** @deprecated Use resolveGumroadBundleSectionIntro — kept for imports that expect funnel copy. */
export const GUMROAD_BUNDLE_SECTION_INTRO = GUMROAD_BUNDLE_SECTION_INTROS.eligible;

/**
 * @param {string} uiState
 * @param {{ code?: string } | null} [status]
 * @returns {string}
 */
export function resolveGumroadBundleSectionIntro(uiState, status = null) {
  if (
    uiState === GUMROAD_BUNDLE_UI_STATE.NOT_ELIGIBLE &&
    status?.code === 'transfer_pending_admin_finalize'
  ) {
    return GUMROAD_BUNDLE_SECTION_INTROS.not_eligible_transfer_pending;
  }
  return GUMROAD_BUNDLE_SECTION_INTROS[uiState] ?? GUMROAD_BUNDLE_SECTION_INTROS.eligible;
}

/** Help panel contexts — marketplace redeem only on checkout (has promo). */
export const GUMROAD_BUNDLE_HELP_CONTEXT = {
  CHECKOUT: 'checkout',
  SUPPORT: 'support',
  TRANSFER_PENDING: 'transfer_pending',
};

/** Primary marketplace CTA label (opens bundle product in a new tab). */
export const GUMROAD_BUNDLE_MARKETPLACE_CTA_LABEL = 'Redeem coupon on bundle';

export const GUMROAD_BUNDLE_MARKETPLACE_CTA_HINT =
  'Opens the bundle product page where you enter your promotion code at checkout.';

export const GUMROAD_BUNDLE_PANEL_COPY = {
  loading: 'Loading bundle access status…',
  errorTitle: 'Could not load status',
  errorFallback: 'Could not load status. Try again later.',
  notConfiguredTitle: 'Not available yet',
  notConfiguredBody:
    'Gumroad bundle access is not available on this server yet. Please try again later or contact support.',
  alreadyEntitledTitle: 'Bundle access active',
  alreadyEntitledLead:
    'You already have Revolt bundle access on this account. No promotion code is needed.',
  alreadyEntitledFoot:
    'You do not need to purchase or redeem the bundle again on this account.',
  hasPromoTitle: 'Your promotion code',
  hasPromoLead:
    'Enter this one-time code at checkout on the bundle product page. It is locked to this account and can only be used once.',
  eligibleGumroadTitle: 'You qualify for a bundle coupon',
  eligibleGumroadLead:
    "We found a Gumroad purchase for this account's email. Request a one-time promotion code, then redeem it on the bundle product page.",
  eligibleTransferTitle: 'Transfer recipient — bundle coupon',
  eligibleTransferLead:
    'This account received a Gumroad UE plugin transfer. Request a one-time promotion code for the Revolt bundle.',
  requestButton: 'Request promotion code',
  requestButtonPending: 'Generating…',
  notEligibleTitle: 'No eligible purchase found',
  notEligibleDefault:
    'No eligible Gumroad purchase was found for this account. If you bought on a different email, transfer the entitlement first, then return here on the recipient account after support finalizes the transfer.',
  transferPendingTitle: 'Transfer pending finalization',
  transferTabCta: 'Open Transfer tab',
  transferTabHint: 'Move your Gumroad purchase to the Revolt account that should receive the bundle coupon.',
  helpTitle: 'Need help?',
  helpCheckoutBody:
    'Confirm you are signed into the same Revolt account the code was generated for, then complete checkout on the bundle product page.',
  helpSupportBody:
    'If you believe your Gumroad purchase should qualify, confirm the purchase email matches this Revolt account, or transfer the entitlement to the recipient account first.',
  helpTransferPendingBody:
    'Support will finalize your transfer. After that, return to this tab on the recipient account to request your bundle promotion code.',
  helpDiscordLead: 'Still stuck? Reach out on Discord:',
  copyButton: 'Copy code',
  copiedButton: 'Copied',
  retryButton: 'Try again',
};

/** Human-readable copy for known API error codes on promo request. */
export const GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES = {
  already_entitled: 'You already have bundle access on this account.',
  already_has_local_revolt: 'You already have bundle access on this account.',
  already_purchased_bundle: 'You already purchased the bundle on this account.',
  not_eligible: 'This account is not eligible for a bundle promotion code.',
  not_gumroad_purchaser: 'No eligible Gumroad purchase was found for this account.',
  transfer_pending_admin_finalize:
    'Your Gumroad UE plugin transfer is recorded but not finalized yet. Support will finalize it; then return here to request your bundle promotion code.',
  bundle_promo_not_configured:
    'Promotion code generation is not enabled on the server yet. You qualify — check back soon or contact support.',
  stripe_not_available: 'Payment integration is temporarily unavailable. Try again later.',
  gumroad_bundle_promo_request_failed: 'Could not generate a promotion code. Try again later.',
};

/** Status intros for Account → Gumroad not_eligible state (keyed by API status code). */
export const GUMROAD_BUNDLE_NOT_ELIGIBLE_INTRO = {
  transfer_pending_admin_finalize:
    GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES.transfer_pending_admin_finalize,
};

export const GUMROAD_BUNDLE_ISSUANCE_PENDING_INTRO =
  GUMROAD_BUNDLE_REQUEST_ERROR_MESSAGES.bundle_promo_not_configured;
